import { ContentsViewData } from '../../util/openContentsWindow';

/**
 * 콘텐츠 타입
 * - Vimeo: 비메오
 * - HTML: HTML (아스펜, 아티큘레이트, 아이스프링으로 추출한 PPT)
 * - Jebspl: 학생 할일을 제외한 jebspl 콘텐츠 (Acts, 홈이 등)
 * - todoView: 학생할일(젭스온, 젭스플, AI인증제)
 * - '': 비정상적인 접근
 */
export type ContentType = 'Vimeo' | 'HTML' | 'Jebspl' | 'todoView' | '';

/** iframe에 렌더링할 콘텐츠 정보. 세션 스토리지에 저장되어 새로고침 시 복원된다. */
export interface ContentsData {
  /** iframe src로 사용할 URL. 빈 문자열이면 "비정상적인 접근"으로 처리된다. */
  contentsUrl: string;
  /** 콘텐츠 종류. 전체화면 버튼 노출 등 UI 분기에 사용된다. */
  contentsType: ContentType;
}

/** 젭스플레이어 iframe이 message 리스너 등록을 마친 뒤 보내는 준비 완료 신호 */
export interface PlayerReadyEvent {
  type: 'PLAYER_READY';
}

/** 젭스플레이어 iframe이 보내는 현재 창 닫기 요청 */
export interface PlayerCloseEvent {
  type: 'PLAYER_CLOSE';
  /**
   * 브라우저 confirm 모달 필요 여부
   * - true: 모달 표시 후 닫기 진행
   * - false: 바로 닫기
   * */
  confirm: boolean;
}

/** 젭스플레이어 iframe이 보내는 메시지 */
export type PlayerMessageEvent = PlayerReadyEvent | PlayerCloseEvent;

/**
 * 오프너 창이 '/contents' 팝업으로 보내는 메시지.
 * 콘텐츠 표시(contentsView/todoView/learningView)는 송신부(util/openContentsWindow)의
 * ContentsViewData를 그대로 재사용하고, 창 닫기(closeEvent, WindowClose가 발신)만 추가한다.
 */
export type PopupMessageEvent = ContentsViewData | { type: 'closeEvent' };

/**
 * 젭스플레이어(과제 콘텐츠) origin.
 * @description
 * REACT_APP_JEBS_PLAYER_URL에서 origin을 파싱하며, 미설정/파싱 실패 시 빈 문자열이다.
 * postMessage 대상 검증(isPlayerHost)과 토큰 전송 대상 판별에 사용한다.
 */
export const PLAYER_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_JEBS_PLAYER_URL ?? '').origin;
  } catch (error) {
    return '';
  }
})();

/** HTML 콘텐츠(아스펜/아티큘레이트/아이스프링으로 추출한 PPT) 호스트 */
export const CONTENT_HOST = 'https://content.jebs.kr';

/**
 * 수업자료(getContentsDatas) 응답 중 iframe URL 생성에 사용하는 필드만 추린 타입.
 * @see ContentsDataType (src/api/utils/lms/lmsApi.ts)
 */
export interface TakeclassContent {
  /** 콘텐츠 종류 */
  type: ContentType;
  /** Vimeo 콘텐츠 재생 URL (ReactPlayer url로 사용) */
  vimeoKey?: string;
  /** HTML 콘텐츠 경로 → `${CONTENT_HOST}/${resourcePath}` */
  resourcePath?: string;
  /** Jebspl(Acts 등) 콘텐츠의 전체 URL */
  jebsplUrl?: string;
}

/**
 * ContentType별 iframe src URL 생성기. 상태에 의존하지 않는 순수 맵.
 * @description todoView/''는 자체 URL을 만들지 않으므로 빈 문자열을 반환한다.
 */
export const getContentUrl: Record<
  ContentType,
  (contents: TakeclassContent) => string
> = {
  Vimeo: (contents) => contents.vimeoKey ?? '',
  HTML: (contents) => `${CONTENT_HOST}/${contents.resourcePath}`,
  Jebspl: (contents) => contents.jebsplUrl ?? '',
  '': () => '',
  todoView: () => '',
};

/**
 * 수업자료 콘텐츠를 type에 맞는 iframe URL로 변환한다.
 * @param contents 수업자료 API 응답(URL 생성 필드)
 * @returns iframe src URL. 알 수 없는 type이면 빈 문자열
 */
export const getTakeclassUrl = (contents: TakeclassContent) =>
  getContentUrl[contents.type]?.(contents) ?? '';

/**
 * URL 문자열에서 origin을 추출한다.
 * @param url 대상 URL
 * @returns origin(예: `https://player.jebs.kr`). 파싱 실패 시 빈 문자열
 */
export const getOriginFromUrl = (url: string) => {
  try {
    return new URL(url).origin;
  } catch (error) {
    return '';
  }
};

/**
 * 주어진 URL이 젭스플레이어 origin과 동일한지 판별한다.
 * @param url 검사할 URL 또는 origin 문자열
 * @returns PLAYER_ORIGIN이 설정되어 있고 origin이 일치하면 true
 */
export const isPlayerHost = (url: string) => {
  try {
    return !!PLAYER_ORIGIN && new URL(url).origin === PLAYER_ORIGIN;
  } catch (error) {
    return false;
  }
};
